import React, { useMemo } from "react"
import { View } from "react-native"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/reusables/ui/tabs"
import { MilestoneRequestList } from "./MilestoneRequestList"
import type { MilestoneType } from "@/generated/graphql"
import { H5 } from "@/components/standard/BFText"

interface RequestsScreenProps {
  milestones: MilestoneType[]
  hubspotLoanId?: string
  portalUrl?: string
}

const categorizeMilestonesByStatus = (milestones: MilestoneType[]) => {
  const todoMilestones: MilestoneType[] = []
  const completedMilestones: MilestoneType[] = []
  const upNextMilestones: MilestoneType[] = []

  milestones.forEach((milestone) => {
    // Only include milestones that have tasks
    if (milestone.tasks.length === 0) return

    const milestoneStatus = milestone.status?.toLowerCase()

    switch (milestoneStatus) {
      case "completed":
        completedMilestones.push(milestone)
        break
      case "blocked":
        upNextMilestones.push(milestone)
        break
      default:
        todoMilestones.push(milestone)
        break
    }
  })

  return {
    todoMilestones,
    completedMilestones,
    upNextMilestones,
  }
}

export const RequestsScreen: React.FC<RequestsScreenProps> = ({
  milestones,
  hubspotLoanId,
  portalUrl,
}) => {
  const [tab, setTab] = React.useState("todo")

  const { todoMilestones, completedMilestones, upNextMilestones } = useMemo(
    () => categorizeMilestonesByStatus(milestones),
    [milestones]
  )

  return (
    <View className="flex-1 bg-background">
      <Tabs
        value={tab}
        onValueChange={setTab}
        orientation={"horizontal"}
        className="flex-1 my-5"
      >
        <TabsList className="mx-4 mb-4 flex-row">
          <TabsTrigger value="todo" className="flex-1">
            <H5>To Do</H5>
          </TabsTrigger>

          <TabsTrigger value="up-next" className="flex-1">
            <H5>Up Next</H5>
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex-1">
            <H5>Completed</H5>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="todo" className="flex-1 px-4">
          <MilestoneRequestList
            milestones={todoMilestones}
            hubspotLoanId={hubspotLoanId}
            status="todo"
            emptyMessage="No active requests at this time. New requests will appear here when milestones become active."
            showFilePortal={true}
            portalUrl={portalUrl}
          />
        </TabsContent>

        <TabsContent value="up-next" className="flex-1 px-4">
          <MilestoneRequestList
            hubspotLoanId={hubspotLoanId}
            milestones={upNextMilestones}
            status="up_next"
            portalUrl={portalUrl}
            emptyMessage="No upcoming requests. Tasks from blocked milestones will appear here."
          />
        </TabsContent>

        <TabsContent value="completed" className="flex-1 px-4">
          <MilestoneRequestList
            milestones={completedMilestones}
            hubspotLoanId={hubspotLoanId}
            status="completed"
            emptyMessage="No completed requests yet. Completed requests will appear here when milestones are finished."
          />
        </TabsContent>
      </Tabs>
    </View>
  )
}
